import { useEffect, useMemo, useState } from 'react';
import { listVendorsByCompanyId, updateVendor } from '../../graphql/companyResourceQueries';
import { filterVendorsByStatus, sortVendorsByName } from '../../utils/companyResourceUtils';
import { Badge, Btn, IconArchive, IconPlus, IconUsers } from '../ui';
import { ArchiveVendorModal } from './ArchiveVendorModal';
import { VendorModal } from './VendorModal';

interface VendorsTabProps {
    isAdmin: boolean;
    companyId: string;
}

export const VendorsTab = ({ isAdmin, companyId }: VendorsTabProps) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [vendors, setVendors] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [editingVendor, setEditingVendor] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [vendorSearch, setVendorSearch] = useState('');
    const [archivingVendor, setArchivingVendor] = useState<{ id: string; name?: string | null } | null>(null);
    const [isArchiving, setIsArchiving] = useState(false);

    const fetchVendors = async () => {
        try {
            setIsLoading(true);
            const data = await listVendorsByCompanyId(companyId);
            setVendors(data);
        } catch (err) {
            console.error('Error fetching vendors:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (companyId) fetchVendors();
    }, [companyId]);

    const handleSave = async () => {
        setShowModal(false);
        setEditingVendor(null);
        await fetchVendors();
    };

    const handleConfirmArchive = async () => {
        if (!archivingVendor) return;
        setIsArchiving(true);
        try {
            await updateVendor({ id: archivingVendor.id, archived: true });
            setVendors((prev) => prev.filter((item) => item.id !== archivingVendor.id));
            setArchivingVendor(null);
        } catch (err) {
            console.error('Error archiving vendor:', err);
        } finally {
            setIsArchiving(false);
        }
    };

    const activeVendors = useMemo(
        () => sortVendorsByName(filterVendorsByStatus(vendors, 'active')),
        [vendors],
    );
    const displayedVendors = useMemo(() => {
        const query = vendorSearch.trim().toLowerCase();
        if (!query) return activeVendors;
        return activeVendors.filter((vendor) => (vendor.name || '').toLowerCase().includes(query));
    }, [activeVendors, vendorSearch]);

    if (isLoading) {
        return <div className="cr-empty-state"><p className="cr-empty-state-text">Loading vendors...</p></div>;
    }

    return (
        <div>
            <div className="cr-toolbar">
                <input
                    id="cr-vendor-search"
                    className="cr-search-input"
                    type="search"
                    aria-label="Filter vendors by name"
                    placeholder="Filter vendors by name..."
                    value={vendorSearch}
                    onChange={(event) => setVendorSearch(event.target.value)}
                />
                {isAdmin && (
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Vendor"
                        buttonOnClick={() => { setEditingVendor(null); setShowModal(true); }}
                    />
                )}
            </div>

            {activeVendors.length === 0 ? (
                <div className="cr-empty-state">
                    <div className="cr-empty-state-icon">👥</div>
                    <p className="cr-empty-state-text">No vendors yet. Add your first vendor to get started.</p>
                </div>
            ) : displayedVendors.length === 0 ? (
                <div className="cr-empty-state">
                    <p className="cr-empty-state-text">No vendors match your search.</p>
                </div>
            ) : (
                <div className="cr-vendor-grid">
                    {displayedVendors.map((vendor: any) => (
                        <div key={vendor.id} className="cr-vendor-card">
                            <div className="cr-vendor-card-header">
                                <h3 className="cr-vendor-card-name">{vendor.name}</h3>
                            </div>
                            {vendor.address && (
                                <div className="cr-vendor-card-row">
                                    <span className="cr-vendor-card-row-label">Address:</span>
                                    <span className="cr-vendor-card-row-value">{vendor.address}</span>
                                </div>
                            )}
                            {vendor.phoneNumber && (
                                <div className="cr-vendor-card-row">
                                    <span className="cr-vendor-card-row-label">Phone:</span>
                                    <span className="cr-vendor-card-row-value">{vendor.phoneNumber}</span>
                                </div>
                            )}
                            {vendor.faxNumber && (
                                <div className="cr-vendor-card-row">
                                    <span className="cr-vendor-card-row-label">Fax:</span>
                                    <span className="cr-vendor-card-row-value">{vendor.faxNumber}</span>
                                </div>
                            )}
                            {vendor.repName && (
                                <div className="cr-vendor-card-row">
                                    <Badge
                                        badgeClasses="badge-info"
                                        buttonSize="sm"
                                        badgeIcon={<IconUsers />}
                                        badgeText={vendor.repName}
                                    />
                                    {vendor.repPhoneNumber && (
                                        <span className="cr-vendor-card-row-value">{vendor.repPhoneNumber}</span>
                                    )}
                                </div>
                            )}
                            {isAdmin && (
                                <div className="cr-vendor-card-actions">
                                    <Btn
                                        variant="secondary-outlined"
                                        buttonSize="sm"
                                        buttonText="Edit"
                                        buttonOnClick={() => { setEditingVendor(vendor); setShowModal(true); }}
                                    />
                                    <Btn
                                        variant="danger-outlined"
                                        buttonSize="sm"
                                        buttonIcon={<IconArchive />}
                                        buttonText="Archive"
                                        buttonOnClick={() => setArchivingVendor(vendor)}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <VendorModal
                    isOpen={showModal}
                    onClose={() => { setShowModal(false); setEditingVendor(null); }}
                    onSave={handleSave}
                    editVendor={editingVendor}
                    companyId={companyId}
                />
            )}

            <ArchiveVendorModal
                vendor={archivingVendor}
                isArchiving={isArchiving}
                onDismiss={() => {
                    if (!isArchiving) setArchivingVendor(null);
                }}
                onConfirmArchive={handleConfirmArchive}
            />
        </div>
    );
};
